/**
 * Dashboard Component with Clerk Authentication (September 2025)
 * 
 * Main authenticated user dashboard showing account overview and key management.
 * Integrates with Convex for real-time data and Clerk for authentication.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from '@tanstack/react-router';
import { api } from '../../convex/_generated/api';
import { CreateChildKeyModal } from './CreateChildKeyModal';
import { ChildApiKeysTable } from './ChildApiKeysTable';
import { UserSyncDebug } from './UserSyncDebug';
import { OnboardingWizard } from './OnboardingWizard';
import { EmptyApiKeys } from './EmptyStates';
import { getConvexHttpUrl } from '../utils/convex';

interface DashboardProps {}

export function Dashboard({}: DashboardProps) {
  const { user: clerkUser, isLoaded: clerkLoaded } = useUser();
  const currentUser = useQuery(api.users.getCurrentUser);
  const serviceKey = useQuery(api.users.getUserServiceKey);
  const childKeys = useQuery(api.apiKeys.list,
    clerkUser ? {
      userId: clerkUser.id,
      environment: 'production',
      includeRevoked: false
    } : 'skip'
  );
  const navigate = useNavigate();

  // Synchronous user creation - no webhook waiting required
  const ensureUser = useMutation(api.users.ensureUser);

  useEffect(() => {
    // When user is signed in but not found in Convex, create user synchronously
    if (clerkLoaded && clerkUser && currentUser === null) {
      console.log("[Dashboard] User authenticated but not in Convex, calling ensureUser");

      // Small delay for token propagation (same as dvina-tanstack pattern)
      const timeoutId = setTimeout(() => {
        ensureUser()
          .then(convexUserId => {
            console.log("[Dashboard] User successfully ensured in Convex:", convexUserId);
          })
          .catch(err => {
            console.error("[Dashboard] Failed to ensure user:", err);
          });
      }, 750); // 750ms delay for token propagation

      return () => clearTimeout(timeoutId);
    }
  }, [clerkLoaded, clerkUser, currentUser, ensureUser]);
  const [isCreateKeyModalOpen, setIsCreateKeyModalOpen] = useState(false);
  const [createdKeys, setCreatedKeys] = useState<Array<{ 
    apiKey: { 
      name: string; 
      keyHash: string; 
      environment: string; 
      rateLimitPerMinute: number 
    }; 
    message: string 
  }>>([]);
  const [showServiceKey, setShowServiceKey] = useState(false);
  const [copiedServiceKey, setCopiedServiceKey] = useState(false);
  const [selectedCodeTab, setSelectedCodeTab] = useState<'nodejs' | 'python' | 'curl' | 'typescript'>('nodejs');
  const [activeTab, setActiveTab] = useState<'actions' | 'testing'>('actions');
  const [selectedChildKey, setSelectedChildKey] = useState<string>('');
  const [selectedKeyMetadata, setSelectedKeyMetadata] = useState<{
    apiKey?: {
      name: string;
      status: string;
      rateLimitPerMinute: number;
      environment: string;
    };
    metadata?: {
      features?: string[];
    };
  } | null>(null);
  const [isOperationLoading, setIsOperationLoading] = useState(false);
  const [operationResult, setOperationResult] = useState<string>('');
  const [jsonError, setJsonError] = useState<string>('');
  const [editableMetadata, setEditableMetadata] = useState<string>('');
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem('kp_onboarding_complete');
  });

  const hasChildKeys = (childKeys?.apiKeys?.length ?? 0) > 0;
  const showWizard = showOnboarding && !hasChildKeys && serviceKey?.serviceApiKey;

  const completeOnboarding = () => {
    localStorage.setItem('kp_onboarding_complete', '1');
    setShowOnboarding(false);
  };

  // Copy service key handler
  const copyServiceKey = async () => {
    if (serviceKey?.serviceApiKey) {
      try {
        await navigator.clipboard.writeText(serviceKey.serviceApiKey);
        setCopiedServiceKey(true);
        setTimeout(() => setCopiedServiceKey(false), 2000);
      } catch (error) {
        console.error('Failed to copy service key:', error);
        alert('Failed to copy service key to clipboard');
      }
    }
  };

  // Handle child key selection
  const handleChildKeySelection = async (keyHash: string) => {
    setSelectedChildKey(keyHash);
    if (keyHash.length > 0 && serviceKey?.serviceApiKey) {
      try {
        const response = await fetch(`${getConvexHttpUrl()}/api/keys/metadata?keyHash=${keyHash}`, {
          headers: {
            'x-api-key': serviceKey.serviceApiKey,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSelectedKeyMetadata(data);
          // Populate editable metadata with formatted JSON
          if (data.metadata) {
            setEditableMetadata(JSON.stringify(data.metadata, null, 2));
          } else {
            // Default metadata structure with creation_date and nickname
            const defaultMetadata = {
              creation_date: new Date().toISOString(),
              nickname: 'my-client-api-key',
              credit_deductions: true,
              balance_checks: true,
              usage_analytics: false,
              admin_access: false
            };
            setEditableMetadata(JSON.stringify(defaultMetadata, null, 2));
          }
        }
      } catch (error) {
        console.error('Failed to fetch key metadata:', error);
      }
    } else {
      setSelectedKeyMetadata(null);
      setEditableMetadata('');
      setIsEditingMetadata(false);
      setJsonError('');
    }
  };

  // Credit operations
  const performCreditOperation = async (operation: 'add' | 'deduct', amount: number = 1) => {
    if (selectedChildKey.length === 0 || !serviceKey?.serviceApiKey || !clerkUser) return;

    setIsOperationLoading(true);
    setOperationResult('');
    
    try {
      const response = await fetch(`${getConvexHttpUrl()}/api/credits/${operation}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': serviceKey.serviceApiKey,
        },
        body: JSON.stringify({
          userId: clerkUser.id,
          amount,
          reason: `Manual ${operation} via Child Key Testing`,
          transactionId: `manual_${operation}_${Date.now()}`
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setOperationResult(`✅ ${operation === 'add' ? 'Added' : 'Deducted'} ${amount} credit(s). New balance: ${data.newBalance}`);
        // Refresh metadata after operation
        handleChildKeySelection(selectedChildKey);
      } else {
        setOperationResult(`❌ Error: ${data.error ?? 'Operation failed'}`);
      }
    } catch (error) {
      console.error(`Credit ${operation} error:`, error);
      setOperationResult(`❌ Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsOperationLoading(false);
      setTimeout(() => setOperationResult(''), 5000);
    }
  };


  // Loading states
  if (!clerkLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-terminal-black">
        <div className="text-terminal-orange">Loading authentication...</div>
      </div>
    );
  }

  if (!clerkUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-terminal-black">
        <div className="text-red-500">Please sign in to access the dashboard.</div>
      </div>
    );
  }

  // ✅ SYNCHRONOUS PATTERN: ensureUser creates user immediately
  // currentUser === undefined means query is loading
  // currentUser === null means user authenticated but ensureUser hasn't completed yet
  if (currentUser === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-terminal-black">
        <div className="text-terminal-orange">Loading user data...</div>
      </div>
    );
  }

  // User authenticated but not created in Convex yet - ensureUser is running
  if (currentUser === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-terminal-black">
        <div className="terminal-box p-8 max-w-md">
          <h2 className="text-xl font-bold text-terminal-orange-bright mb-4">Setting up your account...</h2>
          <p className="text-terminal-orange mb-4">
            Your account is being created. This should only take a moment.
          </p>
          <div className="text-terminal-orange-dim text-sm">
            <div className="animate-pulse">⚡ Creating your account...</div>
          </div>
          <div className="mt-6 text-sm text-terminal-orange-dim">
            <p>This process is automatic and typically completes in under 2 seconds.</p>
            <p className="mt-2">If you see this for more than a few seconds, try refreshing the page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-terminal-black">

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          
          {/* Page Title */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-terminal-orange terminal-glow">Key Palace Dashboard</h1>
          </div>

          {showWizard && (
            <OnboardingWizard
              serviceKey={serviceKey.serviceApiKey}
              onComplete={completeOnboarding}
              onCreateChildKey={() => {
                setIsCreateKeyModalOpen(true);
                completeOnboarding();
              }}
            />
          )}

          {!showWizard && (
          <>
          {/* Account Overview */}
          <div className="terminal-box mb-6">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-terminal-orange-bright mb-4">
                [ ACCOUNT OVERVIEW ]
              </h3>
              
              {currentUser ? (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {/* Free API Keys Quota */}
                  <div className="border border-terminal-orange p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 border border-terminal-orange flex items-center justify-center">
                          <span className="text-terminal-orange font-bold">🎁</span>
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-terminal-orange-dim truncate">
                            Free Client API Keys
                          </dt>
                          <dd className="text-2xl font-bold text-terminal-orange-bright terminal-glow">
                            {100 - (currentUser.apiKeysCount || 0)} remaining
                          </dd>
                          <div className="text-xs text-terminal-orange-dim mt-1">
                            {currentUser.apiKeysCount || 0} / 100 used
                          </div>
                        </dl>
                      </div>
                    </div>
                  </div>

                  {/* API Keys */}
                  <div className="border border-terminal-orange p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 border border-terminal-orange flex items-center justify-center">
                          <span className="text-terminal-orange font-bold">🔑</span>
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-terminal-orange-dim truncate">
                            API Keys
                          </dt>
                          <dd className="text-2xl font-bold text-terminal-orange-bright terminal-glow">
                            {currentUser.apiKeysCount || 0}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>

                  {/* Account Status */}
                  <div className="border border-terminal-orange p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 border border-terminal-orange flex items-center justify-center">
                          <span className="text-terminal-green font-bold">✓</span>
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-terminal-orange-dim truncate">
                            Status
                          </dt>
                          <dd className="text-2xl font-bold text-terminal-green capitalize terminal-glow">
                            {currentUser.user.status}
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-terminal-orange">No user data available</div>
              )}
            </div>
          </div>

          {/* Service API Key Section */}
          <div className="terminal-box mb-6">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-terminal-orange-bright mb-4">
                [ SERVICE API KEY ]
              </h3>
              
              {serviceKey ? (
                <div className="space-y-4">
                  <div className="border border-terminal-amber p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <span className="text-terminal-amber">⚠️</span>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-terminal-amber">
                          Keep your service key secure
                        </h4>
                        <div className="mt-2 text-sm text-terminal-amber">
                          <p>This key allows you to create child API keys and manage your account. Never share it publicly.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border border-terminal-orange-dim p-4">
                    <div className="space-y-3">
                      <div className="text-sm font-medium text-terminal-orange">Service Key</div>
                      
                      {/* Service Key Input with Toggle and Copy */}
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1 max-w-3xl">
                          <input
                            type={showServiceKey ? 'text' : 'password'}
                            value={serviceKey.serviceApiKey || ''}
                            readOnly
                            className="w-full font-mono text-xs border border-terminal-orange-dark bg-terminal-black text-terminal-green px-3 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-terminal-orange-bright"
                          />
                          <button
                            onClick={() => setShowServiceKey(!showServiceKey)}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-terminal-orange hover:text-terminal-orange-bright focus:outline-none"
                          >
                            {showServiceKey ? '👁️' : '👁️‍🗨️'}
                          </button>
                        </div>
                        <button 
                          onClick={copyServiceKey}
                          className={`terminal-button text-sm ${copiedServiceKey ? 'bg-terminal-green text-terminal-black' : ''}`}
                        >
                          {copiedServiceKey ? '✓ Copied!' : '📋 Copy'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* API Usage Examples */}
                  <div className="border border-terminal-orange-dim p-4">
                    <details open className="space-y-4">
                      <summary className="text-sm font-medium text-terminal-orange cursor-pointer hover:text-terminal-orange-bright transition-colors terminal-glow">
                        📚 API Usage Examples
                      </summary>
                      <div className="space-y-4">
                      
                      {/* Tab Buttons */}
                      <div className="flex border-b border-terminal-orange-dark overflow-x-auto">
                        <button
                          onClick={() => setSelectedCodeTab('nodejs')}
                          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap ${
                            selectedCodeTab === 'nodejs'
                              ? 'border-terminal-orange text-terminal-orange-bright bg-terminal-orange-dark bg-opacity-20 terminal-glow'
                              : 'border-transparent text-terminal-orange-dim hover:text-terminal-orange hover:border-terminal-orange-dim'
                          }`}
                        >
                          Node.js
                        </button>
                        <button
                          onClick={() => setSelectedCodeTab('python')}
                          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap ${
                            selectedCodeTab === 'python'
                              ? 'border-terminal-orange text-terminal-orange-bright bg-terminal-orange-dark bg-opacity-20 terminal-glow'
                              : 'border-transparent text-terminal-orange-dim hover:text-terminal-orange hover:border-terminal-orange-dim'
                          }`}
                        >
                          Python
                        </button>
                        <button
                          onClick={() => setSelectedCodeTab('curl')}
                          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap ${
                            selectedCodeTab === 'curl'
                              ? 'border-terminal-orange text-terminal-orange-bright bg-terminal-orange-dark bg-opacity-20 terminal-glow'
                              : 'border-transparent text-terminal-orange-dim hover:text-terminal-orange hover:border-terminal-orange-dim'
                          }`}
                        >
                          curl
                        </button>
                        <button
                          onClick={() => setSelectedCodeTab('typescript')}
                          className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-all duration-200 whitespace-nowrap ${
                            selectedCodeTab === 'typescript'
                              ? 'border-terminal-orange text-terminal-orange-bright bg-terminal-orange-dark bg-opacity-20 terminal-glow'
                              : 'border-transparent text-terminal-orange-dim hover:text-terminal-orange hover:border-terminal-orange-dim'
                          }`}
                        >
                          TypeScript
                        </button>
                      </div>

                      {/* Code Examples */}
                      <div className="relative">
                        <div className="bg-terminal-black border border-terminal-orange-dark rounded p-4 overflow-x-auto">
                          <pre className="text-xs font-mono text-terminal-green">
                            {selectedCodeTab === 'nodejs' && (
`// Node.js example for creating child API keys
const serviceKey = '${showServiceKey ? (serviceKey?.serviceApiKey || 'sk_your_service_key_here') : 'sk_your_service_key_here'}';
const baseUrl = 'https://api.keypalace.dev';

async function createChildKey() {
  try {
    const response = await fetch(\`\${baseUrl}/api/keys/create\`, {
      method: 'POST',
      headers: {
        'x-api-key': serviceKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: 'my-app-key',
        environment: 'production',
        rateLimitPerMinute: 100,
        credits: 50, // Set initial credits
        metadata: {
          creation_date: new Date().toISOString(),
          nickname: 'my-client-api-key',
          credit_deductions: true,
          balance_checks: true,
          usage_analytics: false,
          admin_access: false,
          alertThresholds: {
            lowCredit: 10
          }
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(\`HTTP \${response.status}: \${errorData.error || 'Unknown error'}\`);
    }

    const data = await response.json();
    console.log('Child key created:', data.apiKey);
    return data;
  } catch (error) {
    console.error('Error:', error.message);
    throw error;
  }
}

createChildKey();`
                            )}

                            {selectedCodeTab === 'python' && (
`# Python example for creating child API keys
import requests
import json
from datetime import datetime

service_key = '${showServiceKey ? (serviceKey?.serviceApiKey || 'sk_your_service_key_here') : 'sk_your_service_key_here'}'
base_url = 'https://api.keypalace.dev'

def create_child_key():
    """Create a child API key with metadata and credits"""
    try:
        url = f"{base_url}/api/keys/create"
        
        headers = {
            'x-api-key': service_key,
            'Content-Type': 'application/json'
        }
        
        payload = {
            'name': 'my-app-key',
            'environment': 'production',
            'rateLimitPerMinute': 100,
            'credits': 50,  # Set initial credits
            'metadata': {
                'creation_date': datetime.utcnow().isoformat() + 'Z',
                'nickname': 'my-client-api-key',
                'credit_deductions': True,
                'balance_checks': True,
                'usage_analytics': False,
                'admin_access': False,
                'alertThresholds': {
                    'lowCredit': 10
                }
            }
        }
        
        response = requests.post(url, headers=headers, json=payload)
        response.raise_for_status()
        
        result = response.json()
        print(f"Child key created: {result.get('apiKey')}")
        return result
        
    except requests.exceptions.RequestException as e:
        print(f"Error creating child key: {e}")
        if hasattr(e, 'response') and e.response:
            print(f"Response: {e.response.text}")
        raise

if __name__ == "__main__":
    create_child_key()`
                            )}

                            {selectedCodeTab === 'curl' && (
`# curl example for creating child API keys
curl -X POST https://api.keypalace.dev/api/keys/create \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${showServiceKey ? (serviceKey?.serviceApiKey || 'sk_your_service_key_here') : 'sk_your_service_key_here'}" \\
  -d '{
    "name": "my-app-key",
    "environment": "production",
    "rateLimitPerMinute": 100,
    "credits": 50,
    "metadata": {
      "creation_date": "2025-01-15T10:30:00.000Z",
      "nickname": "my-client-api-key",
      "credit_deductions": true,
      "balance_checks": true,
      "usage_analytics": false,
      "admin_access": false,
      "alertThresholds": {
        "lowCredit": 10
      }
    }
  }'`
                            )}

                            {selectedCodeTab === 'typescript' && (
`// TypeScript example for creating child API keys
interface ChildKeyMetadata {
  creation_date: string;
  nickname: string;
  credit_deductions: boolean;
  balance_checks: boolean;
  usage_analytics: boolean;
  admin_access: boolean;
  alertThresholds: {
    lowCredit: number;
  };
}

interface CreateChildKeyRequest {
  name: string;
  environment: 'development' | 'staging' | 'production';
  rateLimitPerMinute: number;
  credits: number;
  metadata: ChildKeyMetadata;
}

interface CreateChildKeyResponse {
  success: boolean;
  apiKey: string;
  message: string;
}

const serviceKey = '${showServiceKey ? (serviceKey?.serviceApiKey || 'sk_your_service_key_here') : 'sk_your_service_key_here'}';
const baseUrl = 'https://api.keypalace.dev';

async function createChildKey(): Promise<CreateChildKeyResponse> {
  try {
    const requestData: CreateChildKeyRequest = {
      name: 'my-app-key',
      environment: 'production',
      rateLimitPerMinute: 100,
      credits: 50, // Set initial credits
      metadata: {
        creation_date: new Date().toISOString(),
        nickname: 'my-client-api-key',
        credit_deductions: true,
        balance_checks: true,
        usage_analytics: false,
        admin_access: false,
        alertThresholds: {
          lowCredit: 10
        }
      }
    };

    const response = await fetch(\`\${baseUrl}/api/keys/create\`, {
      method: 'POST',
      headers: {
        'x-api-key': serviceKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(\`HTTP \${response.status}: \${errorData.error || 'Unknown error'}\`);
    }

    const data: CreateChildKeyResponse = await response.json();
    console.log('Child key created:', data.apiKey);
    return data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Error:', errorMessage);
    throw error;
  }
}

createChildKey();`
                            )}
                          </pre>
                        </div>
                        
                        {/* Copy Code Button */}
                        <button
                          onClick={() => {
                            let code = '';
                            if (selectedCodeTab === 'nodejs') {
                              code = `// Node.js example for creating child API keys
const serviceKey = '${showServiceKey ? (serviceKey?.serviceApiKey || 'sk_your_service_key_here') : 'sk_your_service_key_here'}';
const baseUrl = 'https://api.keypalace.dev';

async function createChildKey() {
  try {
    const response = await fetch(\`\${baseUrl}/api/keys/create\`, {
      method: 'POST',
      headers: {
        'x-api-key': serviceKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: 'my-app-key',
        environment: 'production',
        rateLimitPerMinute: 100,
        credits: 50, // Set initial credits
        metadata: {
          creation_date: new Date().toISOString(),
          nickname: 'my-client-api-key',
          credit_deductions: true,
          balance_checks: true,
          usage_analytics: false,
          admin_access: false,
          alertThresholds: {
            lowCredit: 10
          }
        }
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(\`HTTP \${response.status}: \${errorData.error || 'Unknown error'}\`);
    }

    const data = await response.json();
    console.log('Child key created:', data.apiKey);
    return data;
  } catch (error) {
    console.error('Error:', error.message);
    throw error;
  }
}

createChildKey();`;
                            } else if (selectedCodeTab === 'python') {
                              code = `# Python example for creating child API keys
import requests
import json
from datetime import datetime

service_key = '${showServiceKey ? (serviceKey?.serviceApiKey || 'sk_your_service_key_here') : 'sk_your_service_key_here'}'
base_url = 'https://api.keypalace.dev'

def create_child_key():
    """Create a child API key with metadata and credits"""
    try:
        url = f"{base_url}/api/keys/create"
        
        headers = {
            'x-api-key': service_key,
            'Content-Type': 'application/json'
        }
        
        payload = {
            'name': 'my-app-key',
            'environment': 'production',
            'rateLimitPerMinute': 100,
            'credits': 50,  # Set initial credits
            'metadata': {
                'creation_date': datetime.utcnow().isoformat() + 'Z',
                'nickname': 'my-client-api-key',
                'credit_deductions': True,
                'balance_checks': True,
                'usage_analytics': False,
                'admin_access': False,
                'alertThresholds': {
                    'lowCredit': 10
                }
            }
        }
        
        response = requests.post(url, headers=headers, json=payload)
        response.raise_for_status()
        
        result = response.json()
        print(f"Child key created: {result.get('apiKey')}")
        return result
        
    except requests.exceptions.RequestException as e:
        print(f"Error creating child key: {e}")
        if hasattr(e, 'response') and e.response:
            print(f"Response: {e.response.text}")
        raise

if __name__ == "__main__":
    create_child_key()`;
                            } else if (selectedCodeTab === 'curl') {
                              code = `# curl example for creating child API keys
curl -X POST https://api.keypalace.dev/api/keys/create \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${showServiceKey ? (serviceKey?.serviceApiKey || 'sk_your_service_key_here') : 'sk_your_service_key_here'}" \\
  -d '{
    "name": "my-app-key",
    "environment": "production",
    "rateLimitPerMinute": 100,
    "credits": 50,
    "metadata": {
      "creation_date": "2025-01-15T10:30:00.000Z",
      "nickname": "my-client-api-key",
      "credit_deductions": true,
      "balance_checks": true,
      "usage_analytics": false,
      "admin_access": false,
      "alertThresholds": {
        "lowCredit": 10
      }
    }
  }'`;
                            } else if (selectedCodeTab === 'typescript') {
                              code = `// TypeScript example for creating child API keys
interface ChildKeyMetadata {
  creation_date: string;
  nickname: string;
  credit_deductions: boolean;
  balance_checks: boolean;
  usage_analytics: boolean;
  admin_access: boolean;
  alertThresholds: {
    lowCredit: number;
  };
}

interface CreateChildKeyRequest {
  name: string;
  environment: 'development' | 'staging' | 'production';
  rateLimitPerMinute: number;
  credits: number;
  metadata: ChildKeyMetadata;
}

interface CreateChildKeyResponse {
  success: boolean;
  apiKey: string;
  message: string;
}

const serviceKey = '${showServiceKey ? (serviceKey?.serviceApiKey || 'sk_your_service_key_here') : 'sk_your_service_key_here'}';
const baseUrl = 'https://api.keypalace.dev';

async function createChildKey(): Promise<CreateChildKeyResponse> {
  try {
    const requestData: CreateChildKeyRequest = {
      name: 'my-app-key',
      environment: 'production',
      rateLimitPerMinute: 100,
      credits: 50, // Set initial credits
      metadata: {
        creation_date: new Date().toISOString(),
        nickname: 'my-client-api-key',
        credit_deductions: true,
        balance_checks: true,
        usage_analytics: false,
        admin_access: false,
        alertThresholds: {
          lowCredit: 10
        }
      }
    };

    const response = await fetch(\`\${baseUrl}/api/keys/create\`, {
      method: 'POST',
      headers: {
        'x-api-key': serviceKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(\`HTTP \${response.status}: \${errorData.error || 'Unknown error'}\`);
    }

    const data: CreateChildKeyResponse = await response.json();
    console.log('Child key created:', data.apiKey);
    return data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Error:', errorMessage);
    throw error;
  }
}

createChildKey();`;
                            }
                            navigator.clipboard?.writeText(code);
                          }}
                          className="absolute top-2 right-2 px-2 py-1 bg-terminal-orange-dark text-terminal-orange text-xs hover:bg-terminal-orange hover:text-terminal-black transition-colors rounded"
                        >
                          📋
                        </button>
                      </div>
                      </div>
                    </details>
                  </div>

                  <div className="text-sm text-terminal-orange-dim">
                    <p><strong className="text-terminal-orange">Usage:</strong> {serviceKey.usage}</p>
                    <p><strong className="text-terminal-orange">Created:</strong> {new Date(serviceKey.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
              ) : serviceKey === null ? (
                <div className="border border-red-500 p-4">
                  <div className="text-red-500">
                    <p className="font-medium">User account not synced</p>
                    <p className="text-sm mt-1">Your user account needs to be created in the database. Check the debug panel in the bottom-right corner to sync your account.</p>
                  </div>
                </div>
              ) : (
                <div className="text-terminal-orange">Loading service key...</div>
              )}
            </div>
          </div>

          {/* Tabbed Actions & Testing */}
          <div className="terminal-box border-2 border-terminal-orange">
            <div className="px-3 sm:px-4 py-4 sm:py-5">
              {/* Tab Headers */}
              <div className="flex border-b-2 border-terminal-orange mb-4 sm:mb-6">
                <button
                  onClick={() => setActiveTab('actions')}
                  className={`px-4 sm:px-6 py-3 text-base sm:text-lg font-bold border-b-4 transition-colors ${
                    activeTab === 'actions'
                      ? 'border-terminal-orange text-terminal-orange-bright terminal-glow'
                      : 'border-transparent text-terminal-orange-dim hover:text-terminal-orange'
                  }`}
                >
                  [ QUICK ACTIONS ]
                </button>
                <button
                  onClick={() => setActiveTab('testing')}
                  className={`px-4 sm:px-6 py-3 text-base sm:text-lg font-bold border-b-4 transition-colors ${
                    activeTab === 'testing'
                      ? 'border-terminal-orange text-terminal-orange-bright terminal-glow'
                      : 'border-transparent text-terminal-orange-dim hover:text-terminal-orange'
                  }`}
                >
                  [ CHILD KEY TESTING ]
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === 'actions' ? (
                <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2">
                  <button
                    onClick={() => setIsCreateKeyModalOpen(true)}
                    className="terminal-button text-base sm:text-lg py-4 sm:py-5 font-bold"
                  >
                    [ CREATE CHILD API KEY ]
                  </button>

                  <button
                    onClick={() => navigate({ to: '/app/dashboard/webhooks' })}
                    className="terminal-button text-base sm:text-lg py-4 sm:py-5 font-bold"
                  >
                    [ WEBHOOKS ]
                  </button>

                  <button className="terminal-button text-base sm:text-lg py-4 sm:py-5 font-bold">
                    [ VIEW API USAGE ]
                  </button>

                  <button className="terminal-button text-base sm:text-lg py-4 sm:py-5 font-bold">
                    [ ADD CREDITS ]
                  </button>

                  <button className="terminal-button text-base sm:text-lg py-4 sm:py-5 font-bold">
                    [ ACCOUNT SETTINGS ]
                  </button>
                </div>
              ) : (
                <div className="space-y-4 sm:space-y-6">
                  {/* Child Key Selection */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-terminal-orange mb-2">
                      Select Child API Key
                    </label>
                    <select
                      value={selectedChildKey}
                      onChange={(e) => handleChildKeySelection(e.target.value)}
                      className="w-full border border-terminal-orange bg-terminal-black text-terminal-orange px-2 sm:px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-terminal-orange-bright"
                    >
                      <option value="">Select a child key...</option>
                      {childKeys?.apiKeys?.map((key) => (
                        <option key={key.id} value={key.keyHash}>
                          {key.name || 'Unnamed Key'} ({key.keyHash.substring(0, 12)}...)
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Selected Key Info */}
                  {selectedKeyMetadata !== null && (
                    <div className="border border-terminal-orange-dim p-3 sm:p-4 space-y-3">
                      <h4 className="text-sm sm:text-base font-medium text-terminal-orange">Key Information</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm">
                        <div>
                          <span className="text-terminal-orange-dim">Name:</span>
                          <span className="ml-2 text-terminal-orange">{selectedKeyMetadata.apiKey?.name}</span>
                        </div>
                        <div>
                          <span className="text-terminal-orange-dim">Status:</span>
                          <span className="ml-2 text-terminal-green">{selectedKeyMetadata.apiKey?.status}</span>
                        </div>
                        <div>
                          <span className="text-terminal-orange-dim">Rate Limit:</span>
                          <span className="ml-2 text-terminal-orange">{selectedKeyMetadata.apiKey?.rateLimitPerMinute}/min</span>
                        </div>
                        <div>
                          <span className="text-terminal-orange-dim">Environment:</span>
                          <span className="ml-2 text-terminal-orange">{selectedKeyMetadata.apiKey?.environment}</span>
                        </div>
                      </div>
                      
                      {/* Metadata */}
                      {selectedKeyMetadata.metadata && (
                        <div>
                          <span className="text-terminal-orange-dim text-xs sm:text-sm">Features:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {/* Handle both old array format and new boolean format */}
                            {selectedKeyMetadata.metadata.features?.map((feature: string) => (
                              <span key={feature} className="px-2 py-1 bg-terminal-orange-dark text-terminal-orange text-xs rounded">
                                {feature.replace('_', ' ')}
                              </span>
                            )) || 
                            // New boolean format
                            Object.entries(selectedKeyMetadata.metadata || {})
                              .filter(([key, value]) => 
                                typeof value === 'boolean' && 
                                value === true && 
                                !['creation_date', 'nickname'].includes(key)
                              )
                              .map(([feature]) => (
                                <span key={feature} className="px-2 py-1 bg-terminal-orange-dark text-terminal-orange text-xs rounded">
                                  {feature.replace('_', ' ')}
                                </span>
                              ))
                            }
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Editable Metadata Section */}
                  {selectedChildKey.length > 0 && editableMetadata && (
                    <div className="border border-terminal-orange-dim p-3 sm:p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm sm:text-base font-medium text-terminal-orange">API Key Metadata</h4>
                        <button
                          onClick={() => setIsEditingMetadata(!isEditingMetadata)}
                          className="text-xs px-2 py-1 border border-terminal-orange-dim bg-terminal-black text-terminal-orange hover:bg-terminal-orange-dark focus:outline-none focus:ring-2 focus:ring-terminal-orange"
                        >
                          {isEditingMetadata ? '[ VIEW ]' : '[ EDIT ]'}
                        </button>
                      </div>
                      
                      {isEditingMetadata ? (
                        <div className="space-y-3">
                          <textarea
                            value={editableMetadata}
                            onChange={(e) => {
                              setEditableMetadata(e.target.value);
                              // Clear error when user starts typing
                              if (jsonError) {
                                setJsonError('');
                              }
                            }}
                            className="w-full h-64 font-mono text-xs border border-terminal-orange-dark bg-terminal-black text-terminal-green px-3 py-2 focus:outline-none focus:ring-2 focus:ring-terminal-orange-bright resize-none"
                            placeholder="Edit JSON metadata..."
                          />
                          
                          {/* JSON Validation Error Display - Below Input */}
                          {jsonError && (
                            <div className="border border-red-500 bg-red-900 bg-opacity-20 p-2 rounded text-xs text-red-400">
                              <span className="font-medium">JSON Error: </span>
                              {jsonError}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={async () => {
                                try {
                                  const parsedMetadata = JSON.parse(editableMetadata);
                                  setJsonError('');
                                  setIsOperationLoading(true);
                                  
                                  // Save metadata via API
                                  const response = await fetch('${getConvexHttpUrl()}/api/keys/metadata', {
                                    method: 'PUT',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'x-api-key': serviceKey?.serviceApiKey || '',
                                    },
                                    body: JSON.stringify({
                                      keyHash: selectedChildKey,
                                      metadata: parsedMetadata,
                                    }),
                                  });

                                  const result = await response.json();
                                  
                                  if (response.ok) {
                                    setIsEditingMetadata(false);
                                    setOperationResult('✅ Metadata saved successfully');
                                    // Refresh the key metadata to show updated values
                                    handleChildKeySelection(selectedChildKey);
                                  } else {
                                    setOperationResult(`❌ Save failed: ${result.error || 'Unknown error'}`);
                                  }
                                  
                                  setTimeout(() => setOperationResult(''), 5000);
                                } catch (error) {
                                  if (error instanceof SyntaxError) {
                                    // JSON parsing error
                                    const errorMessage = error.message || 'Invalid JSON format';
                                    setJsonError(errorMessage);
                                  } else {
                                    // Network or other error
                                    setOperationResult(`❌ Save failed: ${error instanceof Error ? error.message : 'Network error'}`);
                                    setTimeout(() => setOperationResult(''), 5000);
                                  }
                                } finally {
                                  setIsOperationLoading(false);
                                }
                              }}
                              className="terminal-button text-xs px-3 py-1"
                              disabled={isOperationLoading}
                            >
                              {isOperationLoading ? '[ SAVING... ]' : '[ VALIDATE & SAVE ]'}
                            </button>
                            <button
                              onClick={() => {
                                setIsEditingMetadata(false);
                                setJsonError('');
                                // Reset to original metadata
                                if (selectedKeyMetadata?.metadata) {
                                  setEditableMetadata(JSON.stringify(selectedKeyMetadata.metadata, null, 2));
                                }
                              }}
                              className="text-xs px-3 py-1 border border-terminal-orange-dim bg-terminal-black text-terminal-orange hover:bg-terminal-orange-dark focus:outline-none focus:ring-2 focus:ring-terminal-orange"
                            >
                              [ CANCEL ]
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-terminal-black border border-terminal-orange-dark rounded p-3 overflow-x-auto">
                          <pre className="text-xs font-mono text-terminal-green whitespace-pre-wrap">
                            {editableMetadata}
                          </pre>
                        </div>
                      )}
                      
                      <div className="text-xs text-terminal-orange-dim">
                        <p>💡 Edit metadata to customize API key behavior and configuration</p>
                        <p>⚠️ Ensure valid JSON format before saving</p>
                      </div>
                    </div>
                  )}

                  {/* Credit Operations */}
                  {selectedChildKey.length > 0 && (
                    <div className="border border-terminal-orange-dim p-3 sm:p-4 space-y-4">
                      <h4 className="text-sm sm:text-base font-medium text-terminal-orange">Credit Operations</h4>
                      
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => performCreditOperation('add')}
                          disabled={isOperationLoading}
                          className="terminal-button flex-1 text-sm sm:text-base py-2 disabled:opacity-50"
                        >
                          {isOperationLoading ? '[ PROCESSING... ]' : '[ +1 CREDIT ]'}
                        </button>
                        <button
                          onClick={() => performCreditOperation('deduct')}
                          disabled={isOperationLoading}
                          className="terminal-button flex-1 text-sm sm:text-base py-2 disabled:opacity-50"
                        >
                          {isOperationLoading ? '[ PROCESSING... ]' : '[ -1 CREDIT ]'}
                        </button>
                      </div>

                      {/* Operation Result */}
                      {operationResult && (
                        <div className="border border-terminal-orange-dark p-2 sm:p-3 bg-terminal-black">
                          <p className="text-xs sm:text-sm text-terminal-green font-mono">{operationResult}</p>
                        </div>
                      )}

                      <div className="text-xs text-terminal-orange-dim">
                        <p>⚠️ Rate limiting applies: {selectedKeyMetadata?.apiKey?.rateLimitPerMinute ?? 10} requests/minute</p>
                        <p>💡 Operations use your service key authentication</p>
                      </div>
                    </div>
                  )}


                  {/* No Key Selected */}
                  {selectedChildKey.length === 0 && (
                    <div className="border border-terminal-orange-dim p-4 text-center">
                      <p className="text-terminal-orange-dim text-sm">
                        Select a child API key above to test credit operations
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Recently Created Keys */}
          {createdKeys.length > 0 && (
            <div className="terminal-box mt-6">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-terminal-orange-bright mb-4">
                  [ RECENTLY CREATED CHILD KEYS ]
                </h3>
                
                <div className="space-y-4">
                  {createdKeys.map((keyData, index) => (
                    <div key={index} className="border border-terminal-orange-dim p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-terminal-orange">{keyData.apiKey.name}</div>
                          <div className="mt-1 text-xs font-mono border border-terminal-orange-dark px-2 py-1 text-terminal-green inline-block">
                            {keyData.apiKey.keyHash?.substring(0, 20)}...
                          </div>
                          <div className="mt-1 text-xs text-terminal-orange-dim">
                            Environment: {keyData.apiKey.environment} • 
                            Rate Limit: {keyData.apiKey.rateLimitPerMinute}/min
                          </div>
                        </div>
                        <button 
                          onClick={() => navigator.clipboard?.writeText(keyData.apiKey.keyHash ?? '')}
                          className="terminal-button text-xs"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Child API Keys Table */}
          {hasChildKeys ? (
            <ChildApiKeysTable className="mt-6" />
          ) : (
            <div className="mt-6">
              <EmptyApiKeys onCreateKey={() => setIsCreateKeyModalOpen(true)} />
            </div>
          )}
          </>
          )}
        </div>
      </main>

      {/* Create Child Key Modal */}
      {serviceKey && (
        <CreateChildKeyModal
          isOpen={isCreateKeyModalOpen}
          onClose={() => setIsCreateKeyModalOpen(false)}
          serviceKey={serviceKey.serviceApiKey || ''}
          onKeyCreated={(keyData) => {
            setCreatedKeys(prev => [keyData, ...prev]);
          }}
        />
      )}

      {/* User Sync Debug Component - Shows sync status and allows manual sync (dev only) */}
      {process.env.NODE_ENV === 'development' && <UserSyncDebug />}
    </div>
  );
}